import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { message, Spin, Tabs } from "antd";
import { getScheduleImpactForMaterial } from "services/schedule-versions-services";
import { ProjectContext } from "context/ProjectProvider";
import { CiqIconBox } from "components/svg-icons";
import { TMaterialImpactAnalysis } from "./models";
import "./style.scss";
import { TScheduleChange } from "../../../../context/ScheduleChangeContext";
import {
  formatLinkedSubmittalDataForGoverningMaterial,
  getFormattedMaterialImpactData
} from "./utils";
import { ErrorMessages } from "../../../../constants";
import { ImpactSummaryDataType } from "../compare-schedule-2/model";
import ScheduleImpactSummary from "../compare-schedule-2/schedule-impact-summary";
import {
  flattenSubmittalsForGrid,
  flattenMaterialsForGrid
} from "./grid-utils";
import ImpactedSubmittalsGrid from "./impacted-submittals-grid";
import ImpactedMaterialsGrid from "./impacted-materials-grid";
import { DEFAULT_IMPACT_SUMMARY } from "../compare-schedule-2";

const defaultImpactAnalysisData: TMaterialImpactAnalysis = {
  HIGH: {},
  MEDIUM: {},
  LOW: {},
  DELETED: {}
} as TMaterialImpactAnalysis;

function CompareScheduleV3(props: {
  scheduleChange: TScheduleChange;
  isLoadingData: {
    current: boolean;
  };
  setIsLoadingData: React.Dispatch<
    React.SetStateAction<{
      current: boolean;
    }>
  >;
  onScheduleImpactSummaryChange?: (data: ImpactSummaryDataType) => void;
  sourceScheduleName?: string;
  importScheduleName?: string;
}) {
  const {
    scheduleChange,
    isLoadingData,
    setIsLoadingData,
    onScheduleImpactSummaryChange,
    sourceScheduleName,
    importScheduleName
  } = props;
  const [sia, setSia] = useState(defaultImpactAnalysisData);
  const [mia, setMia] = useState(defaultImpactAnalysisData);
  const [scheduleImpactSummary, setscheduleImpactSummary] =
    useState<ImpactSummaryDataType>(DEFAULT_IMPACT_SUMMARY);

  const { isFeatureFlagEnabled } = useContext(ProjectContext);

  const isAffectedMaterialEnabled = isFeatureFlagEnabled("MATERIAL_IMPACT");
  const isShowDeletedActivitiesEnabled = isFeatureFlagEnabled(
    "SHOW_DELETED_ACTIVITIES"
  );

  useEffect(() => {
    const controller = new AbortController();
    const getData = async () => {
      if (
        !scheduleChange.params.importLogId ||
        scheduleChange.params.importLogId === scheduleChange.params.sourceLogId
      )
        return;
      setIsLoadingData({ current: true });
      const res: any = await getScheduleImpactForMaterial(
        scheduleChange.params.importLogId,
        scheduleChange.params.sourceLogId,
        scheduleChange.params.projectToken,
        controller.signal
      );
      if (controller.signal.aborted) return;

      if (res.data) {
        const responseBody = res.data;

        const submittalImpactAnalysis =
          responseBody.success?.impactAnalysis?.submittals ||
          defaultImpactAnalysisData;

        // `|| defaultImpactAnalysisData` (a non-empty literal) makes this always truthy; the false arm is unreachable.
        /* v8 ignore next */
        if (submittalImpactAnalysis) {
          const allMaterialDateBlock =
            responseBody.success.all_material_date_block;

          const formattedSubmittalImpactData =
            formatLinkedSubmittalDataForGoverningMaterial(
              submittalImpactAnalysis,
              allMaterialDateBlock
            );

          setSia(formattedSubmittalImpactData);
        }

        const materialImpactAnalysis =
          responseBody.success?.impactAnalysis?.materials ||
          defaultImpactAnalysisData;
        // `|| defaultImpactAnalysisData` (a non-empty literal) makes this always truthy; the false arm is unreachable.
        /* v8 ignore next */
        if (materialImpactAnalysis) {
          const allSubmittalDateBlock =
            responseBody.success.all_submittal_date_block;
          const formattedMaterialImpcatData = getFormattedMaterialImpactData(
            materialImpactAnalysis,
            allSubmittalDateBlock
          );
          setMia(formattedMaterialImpcatData);
        }

        const latestImpactSummary =
          responseBody.success?.impactSummary || DEFAULT_IMPACT_SUMMARY;
        setscheduleImpactSummary(latestImpactSummary);
        onScheduleImpactSummaryChange?.(latestImpactSummary);
      } else {
        message.error(res?.message || ErrorMessages.SomethingWentWrong);
      }

      setIsLoadingData({ current: false });
    };

    getData();
    return () => {
      controller.abort();
    };
  }, [onScheduleImpactSummaryChange, scheduleChange.params, setIsLoadingData]);

  const submittalGridRows = useMemo(
    () => flattenSubmittalsForGrid(sia, !!isShowDeletedActivitiesEnabled),
    [sia, isShowDeletedActivitiesEnabled]
  );

  const materialGridRows = useMemo(
    () => flattenMaterialsForGrid(mia, !!isShowDeletedActivitiesEnabled),
    [mia, isShowDeletedActivitiesEnabled]
  );

  const [activeTab, setActiveTab] = useState("submittals");

  const onTabChange = useCallback((key: string) => {
    setActiveTab(key);
  }, []);

  if (isLoadingData.current) {
    return (
      <div
        className="w-full h-full flex items-center justify-center"
        data-testid="schedule-impact-loading-indicator"
      >
        <Spin size="large" description="Loading..." />
      </div>
    );
  }

  return (
    <div className="h-full">
      <ScheduleImpactSummary scheduleImpactSummary={scheduleImpactSummary} />
      <hr className="mt-4 border-gray-300" style={{ opacity: "35%" }} />
      <div className="pt-5">
        <div className="h-full w-full schedule-compare-v3">
          <Tabs
            activeKey={activeTab}
            onChange={onTabChange}
            items={[
              {
                key: "submittals",
                label: (
                  <span className="flex items-center gap-2">
                    <CiqIconBox feature="SUBMITTALS" size={15} padding={7} />
                    Impacted Submittals ({submittalGridRows.length})
                  </span>
                )
              },
              ...(isAffectedMaterialEnabled
                ? [
                    {
                      key: "materials",
                      label: (
                        <span className="flex items-center gap-2">
                          <CiqIconBox
                            feature="MATERIALS"
                            size={15}
                            padding={7}
                          />
                          Impacted Materials ({materialGridRows.length})
                        </span>
                      )
                    }
                  ]
                : [])
            ]}
          />
          {activeTab === "submittals" && (
            <ImpactedSubmittalsGrid
              rowData={submittalGridRows}
              isLoading={isLoadingData.current}
              importLogId={scheduleChange.params.importLogId}
              sourceLogId={scheduleChange.params.sourceLogId}
              sourceScheduleName={sourceScheduleName}
              importScheduleName={importScheduleName}
            />
          )}
          {isAffectedMaterialEnabled && activeTab === "materials" && (
            <ImpactedMaterialsGrid
              rowData={materialGridRows}
              isLoading={isLoadingData.current}
              importLogId={scheduleChange.params.importLogId}
              sourceLogId={scheduleChange.params.sourceLogId}
              sourceScheduleName={sourceScheduleName}
              importScheduleName={importScheduleName}
            />
          )}
        </div>
      </div>
    </div>
  );
}
export default CompareScheduleV3;
